/**
 * Single source of truth for which enrichment sessions still deserve live
 * polling; the signal both features use to decide when to keep refreshing.
 *
 * The sessions listing (rows carry a `status`) and the stats endpoint
 * (`recent_sessions` carry a `status`) consult the same rule, so it lives in
 * `_shared` and neither feature owns it. Pure; no React/Redux/fetch.
 */

/**
 * Statuses that still make progress on their own.
 *
 * `PAUSED` is deliberately absent: a paused batch only ever moves again when an
 * admin resumes it, so polling it changes nothing. `session-detail` held the same
 * list under its own name until it dropped it and delegated here.
 */
export const RUNNING_ENRICHMENT_STATUSES = ['IN_PROGRESS', 'PENDING'] as const;

/**
 * A session in a running status that has shown no sign of life for this long is
 * treated as parked, and stops driving the poll. Mirrors `session-detail`'s
 * `STALL_THRESHOLD_MS`.
 *
 * Without this, a single parked batch kept the table polling every 5s forever.
 * `resetPipeline` leaves a batch `PENDING` with `started_at: null` awaiting a
 * manual relaunch; a legitimate resting state that can last for months, and one
 * production batch sat in it for 55 days. That is a state to stop polling on,
 * not one to "repair": the reset is deliberate, so the backend leaves it alone.
 */
export const STALL_THRESHOLD_MS = 30 * 60 * 1000;

/** True when a status is one that still progresses on its own. */
export function isRunningStatus(status: string): boolean {
  const running: readonly string[] = RUNNING_ENRICHMENT_STATUSES;
  return running.includes(status);
}

/** The least a session must expose for the poll decision. */
export interface PollableSession {
  status: string;
  started_at: string | null;
  created_at: string;
}

/**
 * True when a session should keep the page polling: a running status whose last
 * sign of life is recent. A batch that never started falls back to its creation
 * date, which is what retires a parked one.
 *
 * An unparseable timestamp counts as parked: the failure mode of polling forever
 * is far more expensive than that of a table which does not auto-refresh.
 */
export function isLiveSession(session: PollableSession, now: number = Date.now()): boolean {
  if (!isRunningStatus(session.status)) return false;
  const lastSignOfLife = Date.parse(session.started_at ?? session.created_at);
  return !Number.isNaN(lastSignOfLife) && now - lastSignOfLife < STALL_THRESHOLD_MS;
}
